import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { AspectRatio, Box, Button, Card, Container, Flex, HStack, Image, Input, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import { LuArrowLeft, LuChevronLeft, LuChevronRight, LuEye, LuImage, LuSendHorizontal, LuShoppingCart, LuX } from "react-icons/lu";
import { SiZalo } from "react-icons/si";
import { io } from "socket.io-client";
import { useStore } from "contexts/store";
import { useAuth } from "contexts/auth";
import { useApp } from "contexts/app";
import ChatService from "services/chat";
import ImageService from "services/image";
import ProductService from "services/product";
import OrderService from "services/order";
import { socket_url } from "config";
import SingleProductPrice from "@/components/product/price/SingleProductPrice";
import { formatCurrency } from "@/utilities/number";

const chatService = new ChatService();
const imageService = new ImageService();
const productService = new ProductService();
const orderService = new OrderService();
const SOCKET_OPTIONS = {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    timeout: 20000,
};
const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const getInitials = (text = "") => {
    const words = String(text || "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return "S";
    }
    return words.slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("");
};

const formatTime = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
    }).format(date);
};

const formatTimeGroup = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const shouldShowTimeGroup = (list = [], index = 0) => {
    if (index <= 0) {
        return true;
    }

    const current = new Date(list[index]?.create_on || 0).getTime();
    const previous = new Date(list[index - 1]?.create_on || 0).getTime();
    if (!current || !previous) {
        return false;
    }

    const gapMs = Math.abs(current - previous);
    return gapMs >= 15 * 60 * 1000;
};

const normalizeURL = (value = "") => {
    const raw = String(value || "").trim();
    if (!raw) {
        return "";
    }
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const extractURLs = (text = "") => {
    const matches = String(text || "").match(URL_PATTERN) || [];
    const urls = matches.map((item) => normalizeURL(item)).filter(Boolean);
    return [...new Set(urls)];
};

const resolveFileURL = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value === "string") {
        return String(value || "").trim();
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        const picked = value.lg_url || value.md_url || value.url || value.sm_url || "";
        return String(picked || "").trim();
    }

    return "";
};

const getExternalChatType = (url = "") => {
    const value = String(url || "").trim().toLowerCase();
    if (!value) {
        return "";
    }

    if (value.includes("zalo.me") || value.includes("zaloapp.com")) {
        return "zalo";
    }

    if (value.includes("m.me") || value.includes("messenger.com") || value.includes("facebook.com/messages")) {
        return "messenger";
    }

    return "";
};

const firstPositiveNumber = (...values) => {
    for (let index = 0; index < values.length; index++) {
        const n = Number(values[index] || 0);
        if (n > 0) {
            return n;
        }
    }
    return 0;
};

const getImageAttachments = (attachments = []) =>
    (attachments || []).filter((item) => item?.type === "image" && item?.url);

const getNonImageAttachments = (attachments = []) =>
    (attachments || []).filter((item) => !(item?.type === "image" && item?.url));

const renderTextWithLinks = (text = "") => {
    const source = String(text || "");
    const parts = source.split(URL_PATTERN);
    return parts.map((part, index) => {
        if (!part) {
            return null;
        }
        const isLink = URL_PATTERN.test(part);
        URL_PATTERN.lastIndex = 0;
        if (!isLink) {
            return <Fragment key={`${index}-${part}`}>{part}</Fragment>;
        }

        const href = normalizeURL(part);
        return (
            <a key={`${href}-${index}`} href={href} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                {part}
            </a>
        );
    });
};

const playNotificationTone = () => {
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            return;
        }

        const context = new AudioContextClass();
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(920, context.currentTime);
        gainNode.gain.setValueAtTime(0.001, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.2);
        oscillator.connect(gainNode);
        gainNode.connect(context.destination);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.22);
        oscillator.onended = () => context.close();
    } catch (error) {
        // ignore
    }
};

const ChatPage = ({ mode = "page", onClose, seedAttachment, seedToken }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { store } = useStore();
    const { user, showLogin } = useAuth();
    const { setProcessing, setPageTitle, showToast, showAddToCart } = useApp();
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageLoading, setMessageLoading] = useState(false);
    const [content, setContent] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [linkPreviews, setLinkPreviews] = useState({});
    const [productMetaMap, setProductMetaMap] = useState({});
    const [orderMetaMap, setOrderMetaMap] = useState({});
    const [previewImages, setPreviewImages] = useState([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [typingText, setTypingText] = useState("");
    const socketRef = useRef(null);
    const messageListRef = useRef(null);
    const messageEndRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const previewFetchingRef = useRef({});
    const productFetchingRef = useRef({});
    const orderFetchingRef = useRef({});
    const typingEmitTimeoutRef = useRef(null);
    const typingClearTimeoutRef = useRef(null);
    const typingEmitDebounceRef = useRef(null);
    const isOverlay = mode === "overlay";

    const externalChatURL = String(store?.web?.chat_url || "").trim();
    const externalChatType = useMemo(() => getExternalChatType(externalChatURL), [externalChatURL]);

    useEffect(() => {
        setPageTitle("Hỗ trợ khách hàng")
    }, [setPageTitle]);

    useEffect(() => {
        if (isOverlay) {
            if (seedAttachment) {
                setAttachments((prev) => [...prev, seedAttachment]);
            }
            return;
        }

        const routeAttachment = location?.state?.chatAttachment;
        if (routeAttachment) {
            setAttachments((prev) => [...prev, routeAttachment]);
        }
    }, [isOverlay, location?.state, seedAttachment, seedToken]);

    useEffect(() => {
        initChat();

        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().catch(() => null);
        }
    }, [user?.id, isOverlay, externalChatURL]);

    useEffect(() => {
        const allUrls = messages.flatMap((message) => extractURLs(message?.content || ""));
        const uniqueUrls = [...new Set(allUrls)];

        uniqueUrls.forEach(async (url) => {
            if (linkPreviews[url] || previewFetchingRef.current[url]) {
                return;
            }

            previewFetchingRef.current[url] = true;
            try {
                const preview = await chatService.getLinkPreview({ url });
                if (preview?.url) {
                    setLinkPreviews((prev) => ({ ...prev, [url]: preview }));
                }
            } catch (error) {
                // ignore
            }
            delete previewFetchingRef.current[url];
        });
    }, [messages]);

    useEffect(() => {
        const productIDs = [...new Set(
            messages
                .flatMap((message) => message?.attachments || [])
                .filter((att) => att?.type === "product")
                .map((att) => String(att?.meta?.product_id || "").trim())
                .filter(Boolean)
        )];

        productIDs.forEach(async (productID) => {
            if (productFetchingRef.current[productID]) {
                return;
            }

            productFetchingRef.current[productID] = true;
            try {
                const product = await productService.get(productID);
                if (product?.id) {
                    setProductMetaMap((prev) => ({ ...prev, [productID]: product }));
                }
            } catch (error) {
                // ignore
            }
            delete productFetchingRef.current[productID];
        });
    }, [messages]);

    useEffect(() => {
        const orderIDs = [...new Set(
            messages
                .flatMap((message) => message?.attachments || [])
                .filter((att) => att?.type === "invoice")
                .map((att) => String(att?.meta?.order_id || "").trim())
                .filter(Boolean)
        )];

        orderIDs.forEach(async (orderID) => {
            if (orderFetchingRef.current[orderID]) {
                return;
            }

            orderFetchingRef.current[orderID] = true;
            try {
                const order = await orderService.get(orderID);
                if (order?.id) {
                    setOrderMetaMap((prev) => ({ ...prev, [orderID]: order }));
                }
            } catch (error) {
                // ignore
            }
            delete orderFetchingRef.current[orderID];
        });
    }, [messages]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!thread?.id || !token) {
            return;
        }

        const socket = io(socket_url, {
            ...SOCKET_OPTIONS,
            auth: { token },
        });

        socketRef.current = socket;
        socket.on("connect", () => {
            socket.emit("chat:join_thread", { thread_id: thread.id });
        });

        socket.on("reconnect", () => {
            socket.emit("chat:join_thread", { thread_id: thread.id });
        });

        socket.on("connect_error", () => {
            // socket.io will retry automatically
        });

        socket.on("chat:new_message", (message) => {
            if (!message || message.thread_id !== thread.id) {
                return;
            }

            setMessages((prev) => {
                if (prev.some((item) => item.id === message.id)) {
                    return prev;
                }
                return [...prev, message];
            });

            if (message.sender_type !== "customer") {
                playNotificationTone();
                if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                    const body = String(message?.content || "Shop vừa nhắn cho bạn").slice(0, 120);
                    new Notification(title, { body });
                }
                chatService.markRead({ thread_id: thread.id }).catch(() => null);
            }
        });

        socket.on("chat:thread_updated", (nextThread) => {
            if (!nextThread || nextThread.id !== thread.id) {
                return;
            }
            setThread(nextThread);
        });

        socket.on("chat:typing", (payload = {}) => {
            if (!payload || payload.thread_id !== thread.id) {
                return;
            }

            if (payload.sender_type !== "shop_staff") {
                return;
            }

            const label = payload?.sender_name || title;
            if (payload.is_typing) {
                setTypingText(`${label} đang nhập...`);
                if (typingClearTimeoutRef.current) {
                    clearTimeout(typingClearTimeoutRef.current);
                }
                // fallback tự xoá sau 60 giây phòng server không gửi is_typing=false
                typingClearTimeoutRef.current = setTimeout(() => {
                    setTypingText("");
                }, 60000);
                return;
            }

            setTypingText("");
            if (typingClearTimeoutRef.current) {
                clearTimeout(typingClearTimeoutRef.current);
            }
        });

        return () => {
            socket.emit("chat:leave_thread", { thread_id: thread.id });
            if (typingEmitTimeoutRef.current) {
                clearTimeout(typingEmitTimeoutRef.current);
            }
            if (typingEmitDebounceRef.current) {
                clearTimeout(typingEmitDebounceRef.current);
            }
            if (typingClearTimeoutRef.current) {
                clearTimeout(typingClearTimeoutRef.current);
            }
            socket.disconnect();
            socketRef.current = null;
        };
    }, [thread?.id]);

    useEffect(() => {
        const container = messageListRef.current;
        if (!container) {
            return;
        }

        const onScroll = () => {
            const threshold = 80;
            const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            isNearBottomRef.current = distanceToBottom <= threshold;
        };

        onScroll();
        container.addEventListener("scroll", onScroll);

        return () => {
            container.removeEventListener("scroll", onScroll);
        };
    }, [thread?.id]);

    useEffect(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    }, [thread?.id]);

    useEffect(() => {
        if (messageLoading) {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            messageEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        });

        return () => window.cancelAnimationFrame(frame);
    }, [messageLoading, thread?.id]);

    useEffect(() => {
        if (!isNearBottomRef.current) {
            return;
        }
        messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length]);

    // Cleanup: xoá tất cả timeouts khi component unmount
    useEffect(() => {
        return () => {
            if (typingEmitTimeoutRef.current) clearTimeout(typingEmitTimeoutRef.current);
            if (typingEmitDebounceRef.current) clearTimeout(typingEmitDebounceRef.current);
            if (typingClearTimeoutRef.current) clearTimeout(typingClearTimeoutRef.current);
        };
    }, []);

    const onLoginToChat = async () => {
        const loggedUser = await showLogin({ message: "Vui lòng đăng nhập để nhắn tin cho shop" });
        if (loggedUser) {
            initChat();
        }
    };

    const initChat = async () => {
        if (!user) {
            return;
        }

        try {
            const threadData = await chatService.getOrCreateThread();
            setThread(threadData);
            await loadMessages(threadData.id);
        } catch (error) {
            showToast({ type: "error", description: error?.message || "Không tải được hội thoại" });
        }
    };

    const loadMessages = async (threadID) => {
        setMessageLoading(true);
        try {
            const data = await chatService.listMessages({ thread_id: threadID, page: 1, page_size: 80 });
            setMessages(data?.messages || []);
            await chatService.markRead({ thread_id: threadID });
        } catch (error) {
            showToast({ type: "error", description: error?.message || "Không tải được tin nhắn" });
        } finally {
            setMessageLoading(false);
        }
    };

    const onUploadImage = async (event) => {
        const file = event.target?.files?.[0];
        if (!file) {
            return;
        }

        setProcessing(true);
        try {
            const uploaded = await imageService.uploadFile(file);
            const uploadedURL = (
                resolveFileURL(uploaded?.url) ||
                resolveFileURL(uploaded?.data?.url) ||
                resolveFileURL(uploaded?.data) ||
                resolveFileURL(uploaded)
            );

            if (!uploadedURL) {
                showToast({ type: "error", description: "Không lấy được URL ảnh" });
                return;
            }

            setAttachments((prev) => [
                ...prev,
                {
                    type: "image",
                    title: file.name,
                    url: uploadedURL,
                    meta: uploaded,
                },
            ]);
        } catch (error) {
            showToast({ type: "error", description: error?.message || "Tải ảnh thất bại" });
        }
        setProcessing(false);
        event.target.value = "";
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const onSend = async () => {
        if (!thread?.id || sending) {
            return;
        }

        const text = String(content || "").trim();
        if (!text && attachments.length === 0) {
            return;
        }

        setSending(true);
        try {
            const data = await chatService.sendMessage({
                thread_id: thread.id,
                content: text,
                attachments,
            });

            setThread(data?.thread || thread);
            if (data?.message) {
                setMessages((prev) => {
                    if (prev.some((item) => item.id === data.message.id)) {
                        return prev;
                    }
                    return [...prev, data.message];
                });
            }
            setContent("");
            setAttachments([]);
            setTypingText("");
            socketRef.current?.emit("chat:typing", { thread_id: thread.id, is_typing: false });
        } catch (error) {
            showToast({ type: "error", description: error?.message || "Không gửi được tin nhắn" });
        }
        setSending(false);
    };

    const renderAttachment = (attachment) => {
        if (attachment?.type === "image" && attachment?.url) {
            return (
                <Image
                    src={attachment.url}
                    alt={attachment.title || "attachment"}
                    maxH="180px"
                    borderRadius="md"
                    cursor="zoom-in"
                    onClick={() => openImagePreview([attachment.url], 0)}
                />
            );
        }

        if (attachment?.type === "order") {
            return (
                <Box border="1px solid" borderColor="orange.200" bg="orange.50" borderRadius="12px" p={3}>
                    <Text color="orange.700" fontWeight="700" mb={1}>Đơn hàng</Text>
                    <Text color="orange.800">{attachment?.title || `Đơn hàng ${attachment?.meta?.order_id || ""}`}</Text>
                </Box>
            );
        }

        if (attachment?.type === "invoice") {
            const orderID = String(attachment?.meta?.order_id || "").trim();
            const orderData = orderMetaMap[orderID] || {};
            const totalItem = Number(orderData?.total_item ?? attachment?.meta?.total_item ?? 0) || 0;
            const totalAmount = Number(orderData?.total_amount ?? attachment?.meta?.total_amount ?? 0) || 0;
            const shippingAddress =
                orderData?.shipping_address?.address ||
                orderData?.shipping_address?.full_address ||
                attachment?.meta?.shipping_address ||
                "-";

            const openInvoiceDetail = () => {
                if (!orderID) {
                    return;
                }

                if (isOverlay) {
                    onClose?.();
                }
                navigate(`/order/${orderID}`);
            };

            return (
                <Card.Root size="sm" border="1px solid" borderColor="colorPalette.200" borderRadius="12px" w="100%" maxW="100%">
                    <Card.Body style={{ padding: 10 }}>
                        <VStack align="stretch" gap={1}>
                            <Text color="colorPalette.700" fontWeight="700">{attachment?.title || `Hóa đơn ${orderID || ""}`}</Text>
                            <Text fontSize="sm" color="colorPalette.700">Tổng sản phẩm: {totalItem}</Text>
                            <Text fontSize="sm" color="colorPalette.700">Tổng số tiền: {formatCurrency(totalAmount)}</Text>
                            <Text fontSize="sm" color="colorPalette.700" lineClamp={2}>Địa chỉ giao hàng: {shippingAddress}</Text>
                            <HStack justify="flex-end" pt={1}>
                                <Button
                                    size="xs"
                                    borderRadius="full"
                                    variant="outline"
                                    borderColor="colorPalette.500"
                                    color="colorPalette.700"
                                    _hover={{ bg: "colorPalette.100" }}
                                    onClick={openInvoiceDetail}
                                >
                                    Xem
                                </Button>
                            </HStack>
                        </VStack>
                    </Card.Body>
                </Card.Root>
            );
        }

        if (attachment?.type === "product") {
            const productID = String(attachment?.meta?.product_id || "").trim();
            const productData = productMetaMap[productID] || {};
            const productTitle = productData?.name || attachment?.title || attachment?.meta?.product_name || `Sản phẩm ${productID || ""}`;
            const productPrice = firstPositiveNumber(
                productData?.min_price,
                productData?.price,
                productData?.original_price,
                productData?.calculated_price?.calculated_amount,
                productData?.variants?.[0]?.min_price,
                productData?.variants?.[0]?.original_price,
                productData?.variants?.[0]?.price,
                attachment?.meta?.price,
                attachment?.meta?.min_price,
                attachment?.meta?.original_price,
                attachment?.meta?.product_price,
            );
            const imageURL = (
                resolveFileURL(productData?.thumbnail) ||
                resolveFileURL(productData?.image) ||
                resolveFileURL(productData?.images?.[0]) ||
                resolveFileURL(attachment?.url)
            );

            const gotoProductDetail = () => {
                if (!productID) {
                    return;
                }

                if (isOverlay) {
                    onClose?.();
                }
                navigate(`/product/${productID}`);
            };

            const onAddProductToCart = (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!productID) {
                    showToast({ type: "warning", description: "Không tìm thấy mã sản phẩm" });
                    return;
                }
                showAddToCart(productID);
            };

            return (
                <Card.Root
                    w="100%"
                    maxW="100%"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="blackAlpha.200"
                    borderRadius="14px"
                    bg="white"
                    boxShadow="sm"
                >
                    <Card.Body style={{ padding: 10 }}>
                        <VStack align="stretch" gap={2}>
                            <HStack align="stretch" gap={3}>
                                <Box minW="84px" maxW="84px" borderRadius="12px" overflow="hidden" bg="gray.100" border="1px solid" borderColor="blackAlpha.100">
                                    <AspectRatio ratio={1}>
                                        <Image src={imageURL || undefined} alt={productTitle} objectFit="cover" />
                                    </AspectRatio>
                                </Box>

                                <VStack align="stretch" justify="center" gap={1} flex={1} minW={0}>
                                    <Text lineClamp={2} fontSize="sm" fontWeight="600" color="gray.800">
                                        {productTitle}
                                    </Text>
                                    {productPrice > 0
                                        ? <SingleProductPrice price={productPrice} originalPrice={productPrice} />
                                        : <Text color="gray.500" fontSize="sm" mt={1}>Liên hệ</Text>}
                                </VStack>
                            </HStack>

                            <HStack gap={2} pt={1} justify="flex-start" wrap="wrap">
                                <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="blue"
                                    borderRadius="full"
                                    minW="0"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        gotoProductDetail();
                                    }}
                                >
                                    <LuEye />
                                    Xem
                                </Button>

                                <Button
                                    size="xs"
                                    colorScheme="blue"
                                    borderRadius="full"
                                    minW="0"
                                    onClick={onAddProductToCart}
                                >
                                    <LuShoppingCart />
                                    Thêm
                                </Button>
                            </HStack>
                        </VStack>
                    </Card.Body>
                </Card.Root>
            );
        }

        if (attachment?.type === "cart") {
            const route = String(attachment?.meta?.route || "/cart").trim() || "/cart";
            const totalItem = Number(attachment?.meta?.total_item || 0) || 0;
            const totalAmount = Number(attachment?.meta?.total_amount || 0) || 0;
            const openCart = () => {
                if (isOverlay) {
                    onClose?.();
                }
                navigate(route);
            };

            return (
                <Card.Root
                    w="100%"
                    maxW="100%"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="14px"
                    bg="blue.50"
                    boxShadow="sm"
                >
                    <Card.Body style={{ padding: 10 }}>
                        <VStack align="stretch" gap={2}>
                            <Text fontSize="sm" fontWeight="700" color="blue.700">
                                {attachment?.title || "Giỏ hàng hiện tại"}
                            </Text>
                            {!!attachment?.meta?.customer_name && <Text fontSize="xs" color="blue.700">Khách: {attachment.meta.customer_name}</Text>}
                            <Text fontSize="xs" color="blue.700">Số sản phẩm: {totalItem}</Text>
                            <Text fontSize="xs" color="blue.700">Tổng số tiền: {formatCurrency(totalAmount)}</Text>
                            <HStack justify="flex-start" pt={1}>
                                <Button size="xs" colorScheme="blue" borderRadius="full" onClick={openCart}>
                                    <LuShoppingCart />
                                    Đến giỏ hàng
                                </Button>
                            </HStack>
                        </VStack>
                    </Card.Body>
                </Card.Root>
            );
        }

        return <Text>{attachment?.title || attachment?.type || "Đính kèm"}</Text>;
    };

    const title = useMemo(() => store?.web?.title || "Chat hỗ trợ", [store?.web?.title]);
    const shopBadge = useMemo(() => getInitials(title), [title]);
    const lastCustomerMessageID = useMemo(() => {
        for (let index = messages.length - 1; index >= 0; index--) {
            if (messages[index]?.sender_type === "customer") {
                return messages[index].id;
            }
        }
        return null;
    }, [messages]);

    const onBack = () => {
        if (isOverlay) {
            onClose?.();
            return;
        }

        navigate(-1);
    };

    const previewImage = previewImages[previewIndex] || "";

    const openImagePreview = (images = [], index = 0) => {
        if (!Array.isArray(images) || images.length === 0) {
            return;
        }
        setPreviewImages(images);
        setPreviewIndex(index);
    };

    const closeImagePreview = () => {
        setPreviewImages([]);
        setPreviewIndex(0);
    };

    const showPrevImage = () => {
        if (previewImages.length <= 1) {
            return;
        }
        setPreviewIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
    };

    const showNextImage = () => {
        if (previewImages.length <= 1) {
            return;
        }
        setPreviewIndex((prev) => (prev + 1) % previewImages.length);
    };

    const openExternalChat = () => {
        if (!externalChatURL) {
            return;
        }
        window.open(externalChatURL, "_blank", "noopener,noreferrer");
    };

    return (
        <Container maxW={"md"} p={0} h={isOverlay ? "100dvh" : "auto"}>
            <Box
                h={isOverlay ? "100dvh" : "calc(100vh - 96px)"}
                bg="linear-gradient(180deg, #f6f8fc 0%, #eef2f7 100%)"
                borderRadius={isOverlay ? "none" : "24px"}
                overflow="hidden"
                border={isOverlay ? "none" : "1px solid"}
                borderColor="blackAlpha.100"
                display="flex"
                flexDirection="column"
            >
                <Flex
                    align="center"
                    justify="space-between"
                    px={4}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="blackAlpha.100"
                    bg="rgba(255,255,255,0.88)"
                    backdropFilter="blur(12px)"
                >
                    <HStack gap={3} minW={0}>
                        <Button variant="ghost" onClick={onBack} minW="40px" px={0}>
                        <LuArrowLeft />
                    </Button>
                        <Flex
                            w="42px"
                            h="42px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg="colorPalette.500"
                            color="white"
                            fontWeight="700"
                            flexShrink={0}
                        >
                            {shopBadge}
                        </Flex>
                        <Box minW={0}>
                            <Text fontWeight="700" lineClamp={1}>{title}</Text>
                        </Box>
                    </HStack>
                    {externalChatType === "zalo" ? (
                        <Button
                            size="sm"
                            minW="40px"
                            h="40px"
                            borderRadius="full"
                            onClick={openExternalChat}
                            variant="outline"
                            borderColor="colorPalette.500"
                            color="colorPalette.600"
                            bg="white"
                            _hover={{ bg: "colorPalette.50" }}
                            title="Chat Zalo"
                        >
                            <SiZalo />
                        </Button>
                    ) : (
                        <Box w="40px" />
                    )}
                </Flex>

                {!user ? (
                    <Flex flex={1} align="center" justify="center" px={6}>
                        <VStack gap={4} textAlign="center">
                            <Flex
                                w="72px"
                                h="72px"
                                borderRadius="full"
                                align="center"
                                justify="center"
                                bg="colorPalette.500"
                                color="white"
                                fontSize="2xl"
                                fontWeight="700"
                            >
                                {shopBadge}
                            </Flex>
                            <VStack gap={1}>
                                <Text fontWeight="700" fontSize="lg">{title}</Text>
                                <Text fontSize="sm" color="gray.500">
                                    Vui lòng đăng nhập để nhắn tin với shop
                                </Text>
                            </VStack>
                            <Button
                                colorPalette="blue"
                                borderRadius="full"
                                px={8}
                                onClick={onLoginToChat}
                            >
                                Đăng nhập
                            </Button>
                        </VStack>
                    </Flex>
                ) : (
                <Fragment>
                <VStack ref={messageListRef} align="stretch" gap={4} flex={1} overflowY="auto" overflowX="hidden" px={4} py={4}>
                    <VStack gap={1} py={1}>
                        <Flex
                            w="64px"
                            h="64px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg="colorPalette.500"
                            color="white"
                            fontSize="xl"
                            fontWeight="700"
                        >
                            {shopBadge}
                        </Flex>
                        <Text fontWeight="700">{title}</Text>
                    </VStack>

                    {messageLoading ? (
                        <Flex align="center" justify="center" py={10}>
                            <VStack gap={3}>
                                <Spinner size="lg" color="colorPalette.500" borderWidth="3px" />
                                <Text fontSize="sm" color="gray.500">Đang tải tin nhắn...</Text>
                            </VStack>
                        </Flex>
                    ) : null}

                    {!messageLoading && messages.map((item, index) => {
                        const isMine = item.sender_type === "customer";
                        const senderLabel = isMine ? "Bạn" : item.sender_name || title;
                        const isLastMyMessage = isMine && item.id === lastCustomerMessageID;
                        const showSeen = isLastMyMessage && Number(thread?.unread_shop || 0) === 0;
                        const imageAttachments = getImageAttachments(item.attachments || []);
                        const nonImageAttachments = getNonImageAttachments(item.attachments || []);
                        return (
                            <Fragment key={item.id}>
                                {shouldShowTimeGroup(messages, index) && (
                                    <Flex justifyContent="center">
                                        <Text fontSize="xs" color="gray.500" bg="white" px={3} py={1} borderRadius="full" boxShadow="sm">
                                            {formatTimeGroup(item.create_on)}
                                        </Text>
                                    </Flex>
                                )}

                                <Flex justifyContent={isMine ? "flex-end" : "flex-start"}>
                                    <HStack align="flex-end" gap={2} maxW="90%" minW={0} flexDirection={isMine ? "row-reverse" : "row"}>
                                        {!isMine && (
                                            <Flex
                                                w="32px"
                                                h="32px"
                                                borderRadius="full"
                                                align="center"
                                                justify="center"
                                                bg="white"
                                                color="colorPalette.500"
                                                fontSize="xs"
                                                fontWeight="700"
                                                border="1px solid"
                                                borderColor="colorPalette.100"
                                                flexShrink={0}
                                            >
                                                {getInitials(senderLabel)}
                                            </Flex>
                                        )}

                                        <Box minW={0}>
                                            <Text
                                                fontSize="xs"
                                                color="gray.500"
                                                textAlign={isMine ? "right" : "left"}
                                                mb={1}
                                                px={1}
                                            >
                                                {senderLabel} {formatTime(item.create_on)}
                                            </Text>
                                            <Box
                                                maxW="100%"
                                                bg={isMine ? "colorPalette.500" : "white"}
                                                color={isMine ? "white" : "gray.800"}
                                                px={4}
                                                py={3}
                                                borderRadius={isMine ? "20px 20px 6px 20px" : "20px 20px 20px 6px"}
                                                boxShadow="0 10px 30px rgba(15, 23, 42, 0.08)"
                                            >
                                                {!!item.content && <Text whiteSpace="pre-wrap" wordBreak="break-word">{renderTextWithLinks(item.content)}</Text>}
                                                {extractURLs(item.content || "").map((url) => {
                                                    const preview = linkPreviews?.[url];
                                                    if (!preview) {
                                                        return null;
                                                    }

                                                    return (
                                                        <a key={`preview-${item.id}-${url}`} href={preview.url || url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                                                            <Box mt={3} borderRadius="12px" overflow="hidden" border="1px solid" borderColor={isMine ? "whiteAlpha.500" : "gray.200"} bg={isMine ? "whiteAlpha.300" : "gray.50"}>
                                                                {!!preview.image && (
                                                                    <Image src={preview.image} alt={preview.title || "preview"} w="100%" maxH="160px" objectFit="cover" />
                                                                )}
                                                                <Box p={3}>
                                                                    <Text fontWeight="bold" lineClamp={2}>{preview.title || preview.url || url}</Text>
                                                                    {!!preview.description && <Text mt={1} fontSize="sm" opacity={0.9} lineClamp={2}>{preview.description}</Text>}
                                                                    <Text mt={1} fontSize="xs" opacity={0.75} wordBreak="break-all">{preview.site_name || preview.url || url}</Text>
                                                                </Box>
                                                            </Box>
                                                        </a>
                                                    );
                                                })}
                                                {imageAttachments.length > 0 && (
                                                    <Box
                                                        mt={item.content ? 3 : 0}
                                                        display="grid"
                                                        gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                                        gap={2}
                                                    >
                                                        {imageAttachments.map((att, imageIndex) => {
                                                            const imageUrls = imageAttachments.map((img) => img.url).filter(Boolean);
                                                            return (
                                                                <Image
                                                                    key={att.id || `${item.id}-img-${imageIndex}`}
                                                                    src={att.url}
                                                                    alt={att.title || "chat-image"}
                                                                    w="100%"
                                                                    h="88px"
                                                                    objectFit="cover"
                                                                    borderRadius="10px"
                                                                    border="1px solid"
                                                                    borderColor={isMine ? "whiteAlpha.500" : "gray.200"}
                                                                    cursor="zoom-in"
                                                                    onClick={() => openImagePreview(imageUrls, imageIndex)}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                {nonImageAttachments.map((att) => (
                                                    <Box key={att.id} mt={item.content || imageAttachments.length > 0 ? 3 : 0}>
                                                        {renderAttachment(att)}
                                                    </Box>
                                                ))}
                                            </Box>
                                            {showSeen && (
                                                <Text fontSize="xs" color="gray.500" textAlign="right" mt={1} px={1}>
                                                    Đã xem
                                                </Text>
                                            )}
                                        </Box>
                                    </HStack>
                                </Flex>
                            </Fragment>
                        );
                    })}
                    {!!typingText && (
                        <Flex justify="flex-start" px={2}>
                            <HStack gap={2} align="flex-end">
                                <Box
                                    bg="gray.100"
                                    borderRadius="18px 18px 18px 4px"
                                    px={4}
                                    py={2}
                                >
                                    <Text fontSize="sm" color="gray.500">{typingText}</Text>
                                </Box>
                            </HStack>
                        </Flex>
                    )}
                    <Box ref={messageEndRef} h="1px" />
                </VStack>

                <Box px={4} pb={4} pt={3} bg="rgba(255,255,255,0.92)" borderTop="1px solid" borderColor="blackAlpha.100">
                    {attachments.length > 0 && (
                        <HStack gap={2} mb={3} overflowX="auto" pb={1}>
                            {attachments.map((att, idx) => (
                                <Box
                                    key={`${att.type}-${idx}`}
                                    minW="180px"
                                    bg="white"
                                    borderRadius="18px"
                                    px={3}
                                    py={2}
                                    border="1px solid"
                                    borderColor="blackAlpha.100"
                                    boxShadow="sm"
                                >
                                    <HStack justifyContent="space-between" align="start">
                                        <Box flex={1} minW={0}>{renderAttachment(att)}</Box>
                                        <Button size="xs" variant="ghost" onClick={() => removeAttachment(idx)}>Bỏ</Button>
                                    </HStack>
                                </Box>
                            ))}
                        </HStack>
                    )}

                    <Box bg="white" borderRadius="24px" px={3} py={3} boxShadow="0 8px 24px rgba(15, 23, 42, 0.08)">
                        <Stack gap={3}>
                            <Input
                                placeholder="Nhập tin nhắn..."
                                value={content}
                                border="none"
                                boxShadow="none"
                                _focusVisible={{ boxShadow: "none" }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setContent(value);

                                    // Debounce typing indicator: chỉ emit socket event sau 300ms không gõ
                                    if (thread?.id && socketRef.current) {
                                        const isTyping = String(value || "").trim().length > 0;

                                        // Clear timeout cũ
                                        if (typingEmitDebounceRef.current) {
                                            clearTimeout(typingEmitDebounceRef.current);
                                        }

                                        if (isTyping) {
                                            // Debounce: emit socket sau 300ms
                                            typingEmitDebounceRef.current = setTimeout(() => {
                                                socketRef.current?.emit("chat:typing", { thread_id: thread.id, is_typing: true });
                                                
                                                // Auto stop typing sau 1.2s nếu user không tiếp tục gõ
                                                if (typingEmitTimeoutRef.current) {
                                                    clearTimeout(typingEmitTimeoutRef.current);
                                                }
                                                typingEmitTimeoutRef.current = setTimeout(() => {
                                                    socketRef.current?.emit("chat:typing", { thread_id: thread.id, is_typing: false });
                                                }, 1200);
                                            }, 300);
                                        } else {
                                            // Emit stop typing ngay lập tức khi field rỗng
                                            socketRef.current.emit("chat:typing", { thread_id: thread.id, is_typing: false });
                                            
                                            if (typingEmitTimeoutRef.current) {
                                                clearTimeout(typingEmitTimeoutRef.current);
                                            }
                                        }
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        onSend();
                                    }
                                }}
                            />
                            {!!typingText && <Text fontSize="xs" color="gray.500">{typingText}</Text>}

                            <HStack justifyContent="space-between">
                                <HStack gap={2}>
                                    <Button as="label" variant="ghost" size="sm" borderRadius="full">
                                        <LuImage />
                                        Ảnh
                                        <input type="file" accept="image/*" hidden onChange={onUploadImage} />
                                    </Button>
                                </HStack>

                                <Button colorScheme="blue" onClick={onSend} loading={sending} borderRadius="full" px={5}>
                                    <LuSendHorizontal />
                                    Gửi
                                </Button>
                            </HStack>
                        </Stack>
                    </Box>
                </Box>
                </Fragment>
                )}
            </Box>

            {!!previewImage && (
                <Box
                    position="fixed"
                    inset={0}
                    bg="rgba(2, 6, 23, 0.88)"
                    zIndex={2000}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={4}
                    onClick={closeImagePreview}
                >
                    <Box position="relative" maxW="96vw" maxH="90vh" onClick={(event) => event.stopPropagation()}>
                        <Button
                            size="sm"
                            minW="40px"
                            h="40px"
                            borderRadius="full"
                            position="absolute"
                            top={2}
                            right={2}
                            zIndex={2}
                            onClick={closeImagePreview}
                        >
                            <LuX />
                        </Button>

                        {previewImages.length > 1 && (
                            <>
                                <Button
                                    size="sm"
                                    minW="40px"
                                    h="40px"
                                    borderRadius="full"
                                    position="absolute"
                                    left={2}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex={2}
                                    onClick={showPrevImage}
                                >
                                    <LuChevronLeft />
                                </Button>
                                <Button
                                    size="sm"
                                    minW="40px"
                                    h="40px"
                                    borderRadius="full"
                                    position="absolute"
                                    right={2}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex={2}
                                    onClick={showNextImage}
                                >
                                    <LuChevronRight />
                                </Button>
                            </>
                        )}

                        <Image
                            src={previewImage}
                            alt="Xem ảnh"
                            maxW="96vw"
                            maxH="90vh"
                            objectFit="contain"
                            borderRadius="md"
                        />
                    </Box>
                </Box>
            )}
        </Container>
    );
};

export default ChatPage;